import { useQuery } from '@tanstack/react-query';
import { Building2, Globe, Moon, Palette, ShieldCheck, Sun, Monitor } from 'lucide-react';
import { http } from '../api/client';
import { Badge } from '../components/ui/Badge';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useUi } from '../store/ui';
import type { BranchRow } from '../types';
import { PHASE_TITLES } from '../routes/nav';
import { CURRENT_PHASE } from '../layout/Sidebar';

/**
 * Phase 1 settings: appearance (theme + text direction) and a read-only view of
 * the business profile that Phase 7 turns into an editable form.
 */
export function SettingsPage() {
  const { theme, setTheme, direction, setDirection } = useUi();
  const { data } = useQuery({
    queryKey: ['branches'],
    queryFn: () => http.get<{ branches: BranchRow[] }>('/branches'),
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-ink">Settings</h1>
        <p className="mt-1 text-sm text-ink-muted">Appearance is saved on this device. Business setup lands in Phase 7.</p>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Appearance */}
        <Card>
          <CardHeader title="Appearance" subtitle="Stored locally, applied instantly" icon={<Palette className="h-4 w-4" />} />
          <CardBody className="space-y-5">
            <fieldset>
              <legend className="mb-2 text-sm font-medium text-ink">Theme</legend>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: 'light', label: 'Light', icon: Sun },
                  { value: 'dark', label: 'Dark', icon: Moon },
                  { value: 'system', label: 'System', icon: Monitor },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setTheme(opt.value)}
                    aria-pressed={theme === opt.value}
                    className={`flex min-h-touch flex-col items-center gap-1.5 rounded-lg border p-3 text-sm font-medium transition-colors ${
                      theme === opt.value
                        ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                        : 'border-line text-ink-muted hover:bg-surface-2 hover:text-ink'
                    }`}
                  >
                    <opt.icon className="h-5 w-5" />
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-ink-faint">Shortcut: Alt + D toggles light and dark.</p>
            </fieldset>

            <fieldset>
              <legend className="mb-2 text-sm font-medium text-ink">Text direction</legend>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { value: 'ltr', label: 'Left to right', hint: 'English' },
                  { value: 'rtl', label: 'Right to left', hint: 'العربية · עברית' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setDirection(opt.value)}
                    aria-pressed={direction === opt.value}
                    className={`flex min-h-touch items-center justify-between rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      direction === opt.value
                        ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                        : 'border-line text-ink-muted hover:bg-surface-2 hover:text-ink'
                    }`}
                  >
                    <span>{opt.label}</span>
                    <span className="text-xs font-normal opacity-70">{opt.hint}</span>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-ink-faint">
                The entire layout mirrors — sidebar, paddings and icons — without a separate stylesheet.
              </p>
            </fieldset>
          </CardBody>
        </Card>

        {/* Business profile */}
        <Card>
          <CardHeader title="Business profile" subtitle="Read from the database" icon={<Building2 className="h-4 w-4" />} />
          <CardBody>
            <dl className="space-y-3">
            <BusinessRow label="Trading name" value="The Copper Fork" />
            <BusinessRow label="Legal entity" value="The Copper Fork Ltd" />
            <BusinessRow label="VAT number" value="GB123456789" />
            <BusinessRow label="Service charge" value="12.5%" />
            <BusinessRow label="Target food cost" value="30%" />
            <BusinessRow label="Receipt width" value="80 mm thermal" />
            </dl>
            <p className="pt-3 text-xs text-ink-faint">
              Editing these values, receipt templates and printer setup arrives in {PHASE_TITLES[7]}.
            </p>
          </CardBody>
        </Card>

        {/* Branches */}
        <Card className="lg:col-span-2">
          <CardHeader
            title="Branches"
            subtitle="Stock, pricing and reports are all scoped per branch"
            icon={<Globe className="h-4 w-4" />}
            action={<Badge tone="neutral">{data?.branches.length ?? 0} configured</Badge>}
          />
          <CardBody className="p-0">
            <ul className="divide-y divide-line">
              {data?.branches.map((b) => (
                <li key={b.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-2 text-xs font-semibold text-ink-muted">
                    {b.code}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">{b.name}</span>
                    <span className="block truncate text-xs text-ink-faint">
                      {[b.city, b.country_code, b.timezone].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <Badge tone="neutral">{b.currency}</Badge>
                  {b.is_head_office && <Badge tone="brand">head office</Badge>}
                  <Badge tone={b.is_active ? 'success' : 'neutral'}>{b.is_active ? 'active' : 'inactive'}</Badge>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>

        {/* Security */}
        <Card className="lg:col-span-2">
          <CardHeader title="Security posture" subtitle="What Phase 1 enforces" icon={<ShieldCheck className="h-4 w-4" />} />
          <CardBody>
            <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                ['Password hashing', 'argon2id, 19 MiB memory cost'],
                ['Access tokens', 'JWT, 20 minutes'],
                ['Refresh tokens', 'Opaque, hashed at rest, rotated on every use'],
                ['Account lockout', '5 failed attempts → 15 minutes'],
                ['Rate limiting', 'Global API limit plus a tighter sign-in limit'],
                ['Authorisation', '63 permission codes resolved per request'],
                ['Audit trail', 'Append-only, includes denied attempts'],
                ['Transport', 'HTTPS terminated at the platform edge'],
                ['Build phase', PHASE_TITLES[CURRENT_PHASE]],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-line bg-surface-0 p-3">
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">{label}</dt>
                  <dd className="mt-1 text-sm text-ink">{value}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="secondary" disabled title="Arrives in Phase 7">Backup &amp; restore</Button>
              <Button variant="secondary" disabled title="Arrives in Phase 7">Receipt templates</Button>
              <Button variant="secondary" disabled title="Arrives in Phase 7">Printer setup</Button>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function BusinessRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line pb-2 last:border-0">
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="text-sm font-medium text-ink">{value}</dd>
    </div>
  );
}
